/**
 * Mon profil : édition, QR d'échange, export des contacts, centre RGPD.
 *
 * L'export et le QR vivent ici plutôt que dans un menu : « repartir avec ses
 * contacts » est l'une des trois promesses du produit (section 3), elle mérite
 * un emplacement de premier niveau.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appelerApi, envoyer } from '../reseau.ts';
import { useSession } from '../etat.tsx';
import { useLibelles } from '../i18n.tsx';
import { FormulaireProfil } from '../composants/FormulaireProfil.tsx';
import { CaseConsentement } from '../composants/Formulaires.tsx';
import { MonEntreprise } from './compte/MonEntreprise.tsx';
import type { Espace } from './compte/types.ts';

type Onglet = 'profil' | 'qr' | 'contacts' | 'donnees';

export function Profil() {
  const { moi, consentements, recharger, sortir } = useSession();
  const { t } = useLibelles();
  const naviguer = useNavigate();
  const [onglet, setOnglet] = useState<Onglet>('profil');

  return (
    <div className="profil">
      <h1>Mon profil</h1>

      <div className="profil__onglets" role="tablist">
        {(
          [
            ['profil', 'Profil'],
            ['qr', 'Mon QR'],
            ['contacts', 'Mes contacts'],
            ['donnees', 'Mes données'],
          ] as [Onglet, string][]
        ).map(([cle, texte]) => (
          <button
            key={cle}
            role="tab"
            aria-selected={onglet === cle}
            className={`puce cible${onglet === cle ? ' puce--active' : ''}`}
            onClick={() => setOnglet(cle)}
          >
            {texte}
          </button>
        ))}
      </div>

      {onglet === 'profil' && <OngletProfil />}
      {onglet === 'qr' && <OngletQr />}
      {onglet === 'contacts' && <OngletContacts />}
      {onglet === 'donnees' && (
        <OngletDonnees
          consentements={consentements}
          t={t}
          recharger={recharger}
          onEfface={async () => {
            await sortir();
            naviguer('/acces', { replace: true });
          }}
        />
      )}

      {moi && (
        <p className="profil__evenement">
          {moi.evenement_nom} · {moi.lieu_nom}
          {moi.lieu_ville ? `, ${moi.lieu_ville}` : ''}
        </p>
      )}
    </div>
  );
}

/* ————————————————— Mon profil —————————————————
   Deux populations dans la même application :

   · celui qui a un COMPTE EventMatch — la grande majorité depuis que
     l'inscription passe par la fiche de l'événement. Son profil complet
     (photo, logo, entreprise, services, ce qu'il cherche, équipe, réseaux,
     pitch) vit dans `profil_personnel` et le suit d'un événement à l'autre :
     on l'édite ICI, avec le même écran que dans l'espace, plutôt que d'en
     tenir une seconde version qui divergerait ;
   · celui qui a été importé par l'organisateur et n'a pas de compte : il garde
     le formulaire de l'événement, et on lui propose d'ouvrir un compte pour
     que son profil le suive. */

function OngletProfil() {
  const [espace, setEspace] = useState<Espace | null | undefined>(undefined);

  const charger = async () => {
    const r = await appelerApi<Espace>('/api/compte/moi');
    setEspace(r.donnees ?? null);
  };

  useEffect(() => {
    void charger();
  }, []);

  if (espace === undefined) return <p className="profil__chargement">Chargement…</p>;

  if (!espace) {
    return (
      <>
        <FormulaireProfil />
        <section className="carte profil__compte">
          <h2>Gardez votre profil d’un événement à l’autre</h2>
          <p>
            Avec un compte EventMatch, votre photo, votre logo, vos services, votre équipe et vos
            réseaux vous suivent partout — et vous obtenez votre page de rendez-vous personnelle.
          </p>
          <a className="bouton" href="/compte">Créer mon compte</a>
        </section>
      </>
    );
  }

  return (
    <div className="profil__complet">
      <MonEntreprise espace={espace} onChange={charger} sansTitre />
    </div>
  );
}

// --- QR ------------------------------------------------------------------------

function OngletQr() {
  const [image, setImage] = useState<string | null>(null);
  const [jetonScanne, setJetonScanne] = useState('');
  const [resultat, setResultat] = useState<string | null>(null);

  useEffect(() => {
    void appelerApi<{ image: string }>('/api/contacts/qr').then((r) => {
      if (r.donnees) setImage(r.donnees.image);
    });
  }, []);

  const scanner = async () => {
    const r = await envoyer<{ fiche: { prenom: string | null; nom: string | null } }>(
      '/api/contacts/scanner',
      { jeton: jetonScanne.trim() },
    );
    setResultat(
      r.erreur ??
        `Contact échangé avec ${[r.donnees?.fiche.prenom, r.donnees?.fiche.nom].filter(Boolean).join(' ') || 'ce participant'}.`,
    );
    if (!r.erreur) setJetonScanne('');
  };

  return (
    <section className="carte qr">
      <h2>Échanger ses coordonnées</h2>
      <p className="qr__aide">
        Montrez ce code à votre interlocuteur — il le scanne depuis son onglet « Mon QR », et vos
        coordonnées s'échangent dans les deux sens.
      </p>
      {image ? (
        <img src={image} alt="Mon QR code d'échange de contact" className="qr__image" />
      ) : (
        <p>Génération…</p>
      )}

      <details className="qr__scanner">
        <summary className="cible">Scanner le code de quelqu'un d'autre</summary>
        <p className="qr__aide">
          Scannez le QR avec l'appareil photo, puis collez le code obtenu ici.
        </p>
        <div className="qr__saisie">
          <input
            className="champ__saisie"
            value={jetonScanne}
            onChange={(e) => setJetonScanne(e.target.value)}
            placeholder="Code scanné"
          />
          <button className="bouton" disabled={!jetonScanne.trim()} onClick={() => void scanner()}>
            Échanger
          </button>
        </div>
      </details>

      {resultat && <p className="message-info" role="status">{resultat}</p>}
    </section>
  );
}

// --- Contacts -------------------------------------------------------------------

type Recommandation = {
  affichage_id: string;
  titre: string;
  description: string;
  url: string;
  intro: string;
};

function OngletContacts() {
  const [apercu, setApercu] = useState<{ total: number; reduits: number } | null>(null);
  const [recommandation, setRecommandation] = useState<Recommandation | null>(null);

  useEffect(() => {
    void appelerApi<{ total: number; reduits: number }>('/api/contacts/apercu').then((r) => {
      if (r.donnees) setApercu(r.donnees);
    });
  }, []);

  // Section 8 : la surface de recommandation apparaît APRÈS l'export — quand la
  // valeur a été délivrée, jamais avant. Le serveur garantit l'unicité ;
  // l'appel ne se fait qu'au clic sur un export.
  const apresExport = () => {
    if (recommandation) return;
    setTimeout(() => {
      void appelerApi<{ recommandation: Recommandation | null }>(
        '/api/recommandations/surface?surface=apres_export',
      ).then((r) => {
        if (r.donnees?.recommandation) setRecommandation(r.donnees.recommandation);
      });
    }, 1200);
  };

  return (
    <section className="carte export">
      <h2>Vos contacts de l'événement</h2>
      {apercu === null ? (
        <p>Chargement…</p>
      ) : (
        <>
          <p className="export__total">
            {apercu.total} contact{apercu.total === 1 ? '' : 's'} exportable{apercu.total === 1 ? '' : 's'}
          </p>
          {apercu.reduits > 0 && (
            <p className="export__reduits">
              {apercu.reduits} contact{apercu.reduits === 1 ? '' : 's'} sans coordonnées : ces
              personnes n'ont pas consenti au partage. Leur fonction et leur entreprise restent
              dans l'export.
            </p>
          )}
          <div className="export__boutons">
            <a className="bouton" href="/api/contacts/export.vcf" onClick={apresExport}>
              Ajouter au répertoire (vCard)
            </a>
            <a className="bouton bouton--secondaire" href="/api/contacts/export.csv" onClick={apresExport}>
              Télécharger en CSV
            </a>
          </div>
        </>
      )}

      {recommandation && (
        <aside className="recommandation" aria-label="Suggestion d'Elite Lead">
          <p className="recommandation__intro">{recommandation.intro}</p>
          <h3>{recommandation.titre}</h3>
          <p className="recommandation__description">{recommandation.description}</p>
          <a
            className="bouton bouton--secondaire"
            href={recommandation.url}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              void envoyer(`/api/recommandations/${recommandation.affichage_id}/clic`, {})
            }
          >
            En savoir plus
          </a>
        </aside>
      )}
    </section>
  );
}

// --- Données / RGPD ---------------------------------------------------------------

function OngletDonnees({
  consentements,
  t,
  recharger,
  onEfface,
}: {
  consentements: Record<string, boolean>;
  t: (cle: string, defaut?: string) => string;
  recharger: () => Promise<void>;
  onEfface: () => Promise<void>;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const poserConsentement = async (type: string, accorde: boolean) => {
    const r = await envoyer('/api/rgpd/consentements', { type, accorde });
    if (r.erreur) setMessage(r.erreur);
    await recharger();
  };

  const effacer = async () => {
    const r = await envoyer('/api/rgpd/effacement', { confirmation });
    if (r.erreur) {
      setMessage(r.erreur);
      return;
    }
    await onEfface();
  };

  return (
    <section className="carte donnees">
      <h2>{t('rgpd.centre.consentements', 'Vos consentements')}</h2>

      <CaseConsentement
        texte={t('rgpd.partage_coordonnees.texte', "J'autorise le partage de mes coordonnées.")}
        coche={consentements.partage_coordonnees ?? false}
        onChange={(v) => void poserConsentement('partage_coordonnees', v)}
      />
      <CaseConsentement
        texte={t('rgpd.diagnostic_ia.texte', 'Je souhaite mon diagnostic IA gratuit.')}
        coche={consentements.diagnostic_ia ?? false}
        onChange={(v) => void poserConsentement('diagnostic_ia', v)}
      />

      <h2 className="donnees__section">Vos droits</h2>
      <p className="donnees__delai">{t('rgpd.centre.delai', 'Toute demande est traitée sous 30 jours maximum.')}</p>
      <a className="bouton bouton--secondaire" href="/api/rgpd/dossier">
        {t('rgpd.centre.acces', 'Télécharger toutes mes données')}
      </a>

      <details className="donnees__effacement">
        <summary className="cible">{t('rgpd.centre.effacement', 'Supprimer mon compte et mes données')}</summary>
        <p>
          Cette action est immédiate et définitive : votre profil, vos coordonnées et votre nom
          disparaissent. Écrivez <strong>SUPPRIMER</strong> pour confirmer.
        </p>
        <div className="qr__saisie">
          <input
            className="champ__saisie"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="SUPPRIMER"
          />
          <button
            className="bouton bouton--danger"
            disabled={confirmation !== 'SUPPRIMER'}
            onClick={() => void effacer()}
          >
            Effacer définitivement
          </button>
        </div>
      </details>

      <p className="donnees__hebergement">
        {t('rgpd.centre.hebergement', "Vos données sont hébergées dans l'Union européenne.")}
      </p>

      {message && <p className="message-erreur" role="alert">{message}</p>}
    </section>
  );
}
